import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BookReaderPreview from '../components/BookReaderPreview';
import FavoriteButton from '../components/FavoriteButton';
import UserFeedback from '../components/UserFeedback';
import { buildApiUrl, buildAssetUrl, getPlaceholderImage } from '../config/constants';
import { useAuth } from '../context/AuthContext';
import { useReviews } from '../contexts/ReviewsContext';
import { isTokenExpired } from '../utils/auth';

interface Book {
  id: number;
  title: string;
  author: string;
  description: string;
  coverImage?: string;
  category?: string;
  publishedYear?: number;
  language?: string;
  fileName?: string;
  fileType?: string;
}

const BookDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, token, logout } = useAuth();
  const { 
    reviews, 
    loading: reviewsLoading, 
    fetchBookReviews, 
    addReview, 
    checkUserReview 
  } = useReviews();
  
  const [book, setBook] = useState<Book | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showReader, setShowReader] = useState(false);
  const [downloadingBook, setDownloadingBook] = useState(false);
  const [bookContent, setBookContent] = useState<string | null>(null);
  const [userHasReviewed, setUserHasReviewed] = useState(false);
  
  // Încarcă detaliile cărții
  useEffect(() => {
    const fetchBook = async () => {
      try {
        setLoading(true);
        const response = await fetch(buildApiUrl(`/books/${id}`));
        
        if (!response.ok) {
          throw new Error('Nu s-a putut încărca cartea');
        }
        
        const data = await response.json();
        setBook(data);
      } catch (err) {
        console.error('Eroare la încărcarea cărții:', err);
        setError('Nu s-a putut încărca cartea');
      } finally {
        setLoading(false);
      }
    };
    
    fetchBook();
  }, [id]);
  
  useEffect(() => {
    if (id) {
      fetchBookReviews(parseInt(id));
      if (isAuthenticated) {
        checkIfUserReviewed();
      }
    }
  }, [id, isAuthenticated]);
  
  const checkIfUserReviewed = async () => {
    if (!id) return;
    
    try {
      const reviewCheck = await checkUserReview(parseInt(id));
      setUserHasReviewed(reviewCheck?.hasReview || false);
    } catch (error) {
      console.error('Eroare la verificarea recenziei:', error);
    }
  };
  
  // Functie pentru a obtine continutul cărții pentru citire
  const fetchBookContent = async () => {
    if (!book) return null;
    if (!token || isTokenExpired(token)) {
      logout();
      navigate('/login');
      return null;
    }
    
    try {
      const response = await fetch(buildApiUrl(`/books/${id}/content`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.content;
      }

      if (response.status === 401) {
        logout();
        navigate('/login');
      }

      return null;
    } catch (error) {
      console.error("Eroare la încărcarea conținutului cărții:", error);
      return null;
    }
  };
  
  // Descarcă cartea
  const downloadBook = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    if (!book?.fileName) {
      alert('Această carte nu are un fișier asociat pentru descărcare.');
      return;
    }
    
    try {
      setDownloadingBook(true);
      if (!token || isTokenExpired(token)) {
        logout();
        navigate('/login');
        return;
      }

      const response = await fetch(buildApiUrl(`/books/${id}/download`), {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        logout();
        navigate('/login');
        return;
      }

      if (!response.ok) {
        throw new Error('Nu s-a putut descărca fișierul.');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = book.fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Eroare la descărcarea cărții:', error);
    } finally {
      setDownloadingBook(false);
    }
  };
  
  const handleRead = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    // Try to get book content first
    const content = await fetchBookContent();
    if (content) {
      setBookContent(content);
    }
    
    setShowReader(true);
  };
  
  const handleAddReview = async (bookId: number, rating: number, content: string) => {
    try {
      const reviewData = {
        bookId,
        rating,
        content,
        isPublic: true
      };
      
      const result = await addReview(reviewData);
      if (result) {
        setUserHasReviewed(true);
        // Refresh reviews after adding
        await fetchBookReviews(bookId);
      }
    } catch (error) {
      console.error('Eroare la adăugarea recenziei:', error);
    }
  };
  
  const handleLikeReview = (reviewId: string) => {
    // TODO: Implement like functionality
    console.log('Like review:', reviewId);
  };
  
  const handleAddReply = (reviewId: string, content: string) => {
    // TODO: Implement reply functionality
    console.log('Add reply to review:', reviewId, content);
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="pt-24 md:pt-28"></div>
        <div className="flex-grow flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
        <Footer />
      </div>
    );
  }
  
  if (error || !book) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="pt-24 md:pt-28"></div>
        <div className="flex-grow flex items-center justify-center">
          <div className="text-center p-8">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Eroare</h2>
            <p className="text-gray-700 mb-4">{error || 'Nu s-a putut găsi cartea solicitată'}</p>
            <button
              onClick={() => navigate('/books')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Înapoi la cărți
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }
  
  // Funcția pentru a obține URL-ul corect al copertei
  const getCoverUrl = (coverPath: string | undefined) => {
    if (!coverPath) {
      return getPlaceholderImage(book.title, 400, 600);
    }

    return buildAssetUrl(coverPath);
  };
  
  // Obține URL-ul copertei
  const coverImageUrl = getCoverUrl(book.coverImage);
  
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="pt-24 md:pt-28"></div>
      
      <main className="flex-grow container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Hero section cu copertă și detalii de bază */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 py-8 px-6 border-b">
            <div className="flex flex-col lg:flex-row items-start">
              {/* Copertă */}
              <div className="lg:w-1/4 mb-6 lg:mb-0 flex justify-center lg:justify-start">
                <div className="relative">
                  <img 
                    src={coverImageUrl} 
                    alt={`Coperta cărții ${book.title}`} 
                    className="w-64 h-auto rounded-lg shadow-lg"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.onerror = null; // Previne loop infinit
                      target.src = getPlaceholderImage(book.title, 400, 600);
                    }}
                  />
                  
                  {/* Badge pentru gen literar */}
                  {book.category && (
                    <span className="absolute top-4 right-4 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
                      {book.category}
                    </span>
                  )}
                </div>
              </div>
              
              {/* Detalii carte */}
              <div className="lg:w-3/4 lg:pl-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{book.title}</h1>
                <p className="text-lg text-gray-700 mb-6">de <span className="font-medium">{book.author}</span></p>
                
                <div className="prose max-w-none mb-8">
                  <p className="text-gray-700">{book.description || 'Fără descriere disponibilă.'}</p>
                </div>
                
                {/* Butoane acțiune */}
                <div className="flex flex-wrap gap-4">
                  <button
                    onClick={handleRead}
                    className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-bold rounded-lg shadow hover:bg-blue-700 transition"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                    Citește online
                  </button>
                  
                  {book.fileName && (
                    <button
                      onClick={downloadBook}
                      disabled={downloadingBook}
                      className="inline-flex items-center px-6 py-3 bg-green-600 text-white font-bold rounded-lg shadow hover:bg-green-700 transition"
                    >
                      {downloadingBook ? (
                        <>
                          <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Se descarcă...
                        </>
                      ) : (
                        <>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                          Descarcă
                        </>
                      )}
                    </button>
                  )}
                  
                  <div className="flex items-center">
                    <FavoriteButton
                      bookId={book.id}
                      size="large"
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-600">
                      {isAuthenticated ? 'Adaugă la favorite' : 'Autentifică-te pentru favorite'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Detalii suplimentare */}
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Informații carte */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4">Detalii carte</h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Autor</h3>
                    <p className="mt-1 text-lg text-gray-900">{book.author}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Categorie</h3>
                    <p className="mt-1 text-lg text-gray-900">{book.category || 'Necategorizat'}</p>
                  </div>
                  
                  {book.publishedYear && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Anul publicării</h3>
                      <p className="mt-1 text-lg text-gray-900">{book.publishedYear}</p>
                    </div>
                  )}
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Limba</h3>
                    <p className="mt-1 text-lg text-gray-900">{book.language || 'Română'}</p>
                  </div>
                  
                  {book.fileType && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Format</h3>
                      <p className="mt-1 text-lg text-gray-900">{book.fileType.toUpperCase()}</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Recenzii și evaluări */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4">Recenzii și evaluări</h2>
                {reviewsLoading ? (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-700">Se încarcă recenziile...</p>
                  </div>
                ) : (
                  <UserFeedback
                    bookId={book.id}
                    bookTitle={book.title}
                    ratings={{
                      rating: reviews.averageRating || 0,
                      userCount: reviews.totalReviews || 0,
                      breakdown: {
                        5: 0,
                        4: 0,
                        3: 0,
                        2: 0,
                        1: 0
                      }
                    }}
                    reviews={reviews.reviews?.map(review => ({
                      id: review.id.toString(),
                      userName: review.User ? 
                        `${review.User.firstName || ''} ${review.User.lastName || ''}`.trim() || 
                        review.User.username : 
                        'Utilizator anonim',
                      rating: review.rating,
                      date: new Date(review.createdAt).toLocaleDateString('ro-RO'),
                      content: review.content || '',
                      likes: 0,
                      hasLiked: false,
                      replies: []
                    })) || []}
                    userHasReviewed={userHasReviewed}
                    onAddReview={handleAddReview}
                    onLikeReview={handleLikeReview}
                    onAddReply={handleAddReply}
                  />
                )}
              </div>
            </div>
            
            {/* Recomandări */}
            <div className="mt-12">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Cărți similare</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
                <div className="text-center">
                  <p className="text-gray-500 italic">Se vor încărca recomandări bazate pe preferințele tale.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      {/* Reader pentru carte */}
      {showReader && book && (
        <BookReaderPreview
          book={{
            id: book.id,
            title: book.title,
            author: book.author,
            coverImage: coverImageUrl,
            content: bookContent || undefined,
            fileName: book.fileName,
            fileType: book.fileType,
            currentPage: 1
          }}
          onClose={() => setShowReader(false)}
        />
      )}
      
      <Footer />
    </div>
  );
};

export default BookDetailsPage; 
